import { csrfFetch } from "./csrf_fetch"
import { showRetryBanner } from "./retry_banner"

// Fire-and-forget writes (quick-complete, drag reorder, folder drop, remote
// submits) update the page optimistically, then send the request. Without a
// check here a 502 during a deploy, an expired session, or a dropped
// connection left the optimistic change on screen and the user believing it
// saved.
//
// Sends the write with csrfFetch and renders the turbo-stream response. On
// failure it says so in the same persistent banner turbo_retry uses for failed
// form submissions; with `revert: true` it first reloads the page so the
// optimistic change is undone. The success path is unchanged: one request.

const SIGN_IN_PATH = "/sign_in"
const NOT_SAVED_MESSAGE = "Your change wasn't saved. Please refresh the page and try again."

export async function turboStreamWrite(url, options, { message = NOT_SAVED_MESSAGE, revert = false } = {}) {
  let response
  try {
    response = await csrfFetch(url, options)
  } catch {
    // Unreachable server: a reload would fail too, so just say so.
    return showRetryBanner(message, { final: true })
  }

  // Expired session. Re-visit the page so the server redirects to sign-in
  // with a return_to for where the user was (as turbo_frame_retry does).
  if (response.status === 401 || isSignInRedirect(response)) return reloadPage()

  if (!response.ok) {
    if (!revert) return showRetryBanner(message, { final: true })

    // The reload replaces #flash, so show the message once it has rendered.
    document.addEventListener("turbo:load", () => showRetryBanner(message, { final: true }), { once: true })
    return reloadPage()
  }

  window.Turbo.renderStreamMessage(await response.text())
}

function reloadPage() {
  window.Turbo.visit(window.location.href, { action: "replace" })
}

function isSignInRedirect(response) {
  return response.redirected && new URL(response.url, window.location.origin).pathname === SIGN_IN_PATH
}
